import React, { useEffect, useRef, useState } from 'react';
import { Calendar, Music, Video } from 'lucide-react';
import { IS_HALLOWEEN } from '../theme';
import { Cobweb } from './Halloween/Cobweb';
import './Halloween/halloween.css';
import { lowestHourlyRate, MIN_SESSION_HOURS, BOOKING_DEPOSIT } from '../lib/bookingRules';

const Hero = () => {
  const videoRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate parallax effect
  const parallaxOffset = scrollY * 0.5;

  return (
    <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden" style={{ paddingTop: 'calc(5rem + env(safe-area-inset-top, 0px))' }}>
      {/* Parallax Video Background */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          transform: `translateY(${parallaxOffset}px)`,
          willChange: 'transform'
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          preload="metadata"
          poster="/videos/studio-tour-poster.jpg"
          loop
          muted
          playsInline
          className={`absolute top-1/2 left-1/2 w-full h-full -translate-x-1/2 -translate-y-1/2 z-10 ${IS_HALLOWEEN ? 'hw-video' : ''}`}
          style={{
            objectFit: 'cover',
            scale: '1.08',
            filter: 'brightness(1.1) contrast(1.05)'
          }}
          onError={() => {
            console.log('Video failed to load, showing fallback');
            setVideoError(true);
          }}
        >
          <source src="/videos/studio-tour-720.mp4" type="video/mp4" />
        </video>

        {/* Fallback gradient background - only show if video fails to load */}
        {videoError && (
          <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950 to-black">
            <div className={`absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_63dac94a-0f2a-4ccd-9e98-d528f614ae89/artifacts/6clph73j_493F5211-773A-4308-B0DA-5C2A4C088AFB.jpeg')] bg-cover bg-center opacity-20 ${IS_HALLOWEEN ? 'hw-video' : ''}`}></div>
          </div>
        )}

        {/* Dark Overlay for text readability */}
        <div className={`absolute inset-0 z-20 ${IS_HALLOWEEN ? 'bg-black/20' : 'bg-black/40'}`}></div>
        {IS_HALLOWEEN && (
          <>
            <Cobweb className="hw-web top-0 left-0" />
            <Cobweb className="hw-web top-0 right-0" flip />
          </>
        )}
        
        {/* Gradient overlay for aesthetics */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50 z-20"></div>
      </div>

      {/* Animated gradient orbs - lighter so they don't overpower video */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute top-40 right-10 w-72 h-72 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-cyan-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>

      {/* Content */}
      <div className="relative z-30 container mx-auto px-4 lg:px-8 py-10 md:py-20 text-center">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-purple-900/30 backdrop-blur-sm border border-purple-500/30 rounded-full px-4 py-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-purple-300 text-sm font-medium">24/7 Booking Available</span>
          </div>

          {/* Headline */}
          <h1 className={`text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight ${IS_HALLOWEEN ? 'hw-flicker' : ''}`}>
            High Quality Studios
          </h1>
          {IS_HALLOWEEN && (
            <p data-testid="hero-halloween-tag" className="hw-font text-3xl md:text-4xl text-orange-500 -mt-2 mb-4">Happy Halloween</p>
          )}
          
          <p className="text-lg md:text-2xl text-gray-200 max-w-2xl mx-auto leading-relaxed">
            Recording studio in East Williamsburg, Brooklyn. Pick an engineer, see your price, lock in your session in minutes.
          </p>

          {/* Primary CTA */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <a
              href="/book"
              data-testid="hero-book-recording-btn"
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full font-bold text-base md:text-lg tracking-wide hover:from-purple-700 hover:to-blue-700 transition-transform hover:scale-105 shadow-xl shadow-purple-500/40"
            >
              BOOK A RECORDING SESSION
            </a>
            <a
              href="#pricing"
              data-testid="hero-view-pricing-btn"
              className="px-6 py-3 text-white/90 rounded-full font-semibold text-sm md:text-base border border-white/20 bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
            >
              See Pricing
            </a>
          </div>

          {/* Pricing Highlight */}
          <div data-testid="hero-pricing" className="flex flex-wrap justify-center gap-2 text-xs md:text-sm text-gray-200">
            <span className="px-3 py-1 rounded-full bg-black/40 border border-white/10">From <b className="text-purple-300">${lowestHourlyRate}/hr</b></span>
            <span className="px-3 py-1 rounded-full bg-black/40 border border-white/10">{MIN_SESSION_HOURS}-hr minimum</span>
            <span className="px-3 py-1 rounded-full bg-black/40 border border-white/10"><b className="text-blue-300">${BOOKING_DEPOSIT}</b> deposit holds your spot</span>
          </div>

          {/* Features */}
          <div className="flex flex-wrap justify-center gap-3 text-xs md:text-sm">
            <div className="flex items-center space-x-2 bg-black/40 backdrop-blur-sm px-4 py-2 rounded-full border border-purple-500/20">
              <Music size={16} className="text-purple-400" />
              <span className="text-gray-300">Recording, Mixing & Mastering</span>
            </div>
            <div className="flex items-center space-x-2 bg-black/40 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-500/20">
              <Video size={16} className="text-blue-400" />
              <span className="text-gray-300">Video & Reels</span>
            </div>
            <div className="flex items-center space-x-2 bg-black/40 backdrop-blur-sm px-4 py-2 rounded-full border border-cyan-500/20">
              <Calendar size={16} className="text-cyan-400" />
              <span className="text-gray-300">No account needed to book</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-30">
        <div className="w-6 h-10 border-2 border-purple-400/50 rounded-full flex justify-center">
          <div className="w-1 h-2 bg-purple-400 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>

      {/* Fade to black at bottom for smooth transition to Services section */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-black via-black/80 to-transparent z-20"></div>
    </section>
  );
};

export default Hero;